import { getStatusForId as getStatus } from '@ms/yammer-data-support';
import { CampaignColor } from '@ms/yammer-graphql';

import { Campaign, User, UserHeader, YammerState } from '../../state/types';
import { createFindById, createFindByIdThrows } from '../findById';

export const findById = createFindById<UserHeader>('userHeaders');
export const findByIdThrows = createFindByIdThrows<UserHeader>('userHeaders');

type GetStatusForId = (state: YammerState, userId: User['id']) => ReturnType<typeof getStatus>;
export const getStatusForId: GetStatusForId = (state, userId) => getStatus(state.userHeaders, userId);

type GetTotalFollowers = (state: YammerState, userId: User['id']) => number;
export const getTotalFollowers: GetTotalFollowers = (state, userId) =>
  findByIdThrows(state, userId)?.totalFollowers || 0;

type GetTotalFollowedUsers = (state: YammerState, userId: User['id']) => number;
export const getTotalFollowedUsers: GetTotalFollowedUsers = (state, userId) =>
  findByIdThrows(state, userId)?.totalFollowedUsers || 0;

type ViewerCanAccessUserStoryline = (state: YammerState, userId: User['id'] | undefined) => boolean;
export const viewerCanAccessUserStoryline: ViewerCanAccessUserStoryline = (state, userId) =>
  (userId && findById(state, userId)?.viewerCanAccessStoryline) || false;

type ViewerCanStartStorylineThread = (state: YammerState, userId: User['id'] | undefined) => boolean;
export const viewerCanStartStorylineThread: ViewerCanStartStorylineThread = (state, userId) =>
  (userId && findById(state, userId)?.viewerCanStartStorylineThread) || false;

type TotalPosts = (state: YammerState, userId: User['id'] | undefined) => number;
export const getTotalPosts: TotalPosts = (state, userId) => (userId ? findById(state, userId)?.totalPosts || 0 : 0);

type TotalMomentsCount = (state: YammerState, userId: User['id'] | undefined) => number;
export const getTotalMomentsCount: TotalMomentsCount = (state, userId) =>
  userId ? findById(state, userId)?.totalMomentsCount || 0 : 0;

type GetIsLeader = (state: YammerState, userId: User['id']) => boolean;
export const getIsLeader: GetIsLeader = (state, userId) => !!findById(state, userId)?.isLeader;

type ViewerCanEditCoverImage = (state: YammerState, userId: User['id']) => boolean;
export const viewerCanEditCoverImage: ViewerCanEditCoverImage = (state, userId) =>
  findByIdThrows(state, userId)?.viewerCanEditCoverImage || false;

type ViewerCanDeleteCoverImage = (state: YammerState, userId: User['id']) => boolean;
export const viewerCanDeleteCoverImage: ViewerCanDeleteCoverImage = (state, userId) =>
  findByIdThrows(state, userId)?.viewerCanDeleteCoverImage || false;

type ViewerCanAccessLeaderEmployeesFeed = (state: YammerState, userId: User['id']) => boolean;
export const viewerCanAccessLeaderEmployeesFeed: ViewerCanAccessLeaderEmployeesFeed = (state, userId) =>
  !!findById(state, userId)?.viewerCanAccessLeaderEmployeesFeed;

type CampaignSashTitle = (state: YammerState, userId: User['id']) => string;
export const campaignSashTitle: CampaignSashTitle = (state, userId) => findById(state, userId)?.campaignSashTitle || '';

type CampaignSashColor = (state: YammerState, userId: User['id']) => CampaignColor | undefined;
export const campaignSashColor: CampaignSashColor = (state, userId) => findById(state, userId)?.campaignSashColor;

type CampaignId = (state: YammerState, userId: User['id']) => Campaign['id'] | undefined;
export const campaignId: CampaignId = (state, userId) => findById(state, userId)?.campaignId;

type CampaignIsOfficial = (state: YammerState, userId: User['id']) => boolean;
export const campaignIsOfficial: CampaignIsOfficial = (state, userId) =>
  findById(state, userId)?.campaignIsOfficial || false;

type CampaignIsSponsored = (state: YammerState, userId: User['id']) => boolean;
export const campaignIsSponsored: CampaignIsSponsored = (state, userId) =>
  !!findById(state, userId)
    ?.campaignSponsors?.map((sponsor) => sponsor.nodeId)
    .includes(userId);
